import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { queryOptions, useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { designQueryKeys, invalidateDesignQueries } from '../designs/designs.query'
import {
  createAlbum,
  deleteAlbum,
  getAlbum,
  getAlbums,
  updateAlbum,
  type AlbumOrganizationScope,
  type AlbumScope,
  type AlbumShowKeyEntries,
} from './albums.api'
import type { AlbumIdInput, CreateAlbumInput, UpdateAlbumInput } from './albums.types'

/** The key entries each fetcher reads; the scoped key builders below set them. */
type AlbumIndexQueryKey = [AdminQueryRoot & AlbumOrganizationScope]
type AlbumShowQueryKey = [AdminQueryRoot & AlbumShowKeyEntries]

/** Without an organization, the root key matches every organization; hooks fall back to the route's. */
interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const albumQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'albums' }, { organizationId }),
  ],
  index: ({ organizationId }: AlbumOrganizationScope): AlbumIndexQueryKey =>
    buildScopedAdminQueryKey(albumQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
    }),
  show: ({ organizationId, albumId }: AlbumScope): AlbumShowQueryKey =>
    buildScopedAdminQueryKey(albumQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'show',
      albumId: Number(albumId),
    }),
}

export const albumQueries = {
  index: ({ organizationId }: AlbumOrganizationScope) =>
    queryOptions({
      queryKey: albumQueryKeys.index({ organizationId }),
      queryFn: getAlbums,
    }),
  show: ({ organizationId, albumId }: AlbumScope) =>
    queryOptions({
      queryKey: albumQueryKeys.show({ organizationId, albumId }),
      queryFn: getAlbum,
    }),
}

export function useAlbum(
  albumId: AlbumIdInput,
  {
    organizationId,
    ...params
  }: OrganizationScope & QueryParams<ReturnType<typeof albumQueries.show>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...albumQueries.show({ organizationId: resolvedOrganizationId, albumId }),
    ...params,
  })
}

export function useCreateAlbumMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: CreateAlbumInput) => createAlbum({ organizationId, ...values }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: albumQueryKeys.all({ organizationId }) }),
        queryClient.invalidateQueries({
          queryKey: designQueryKeys.default({ organizationId }),
          refetchType: 'all',
        }),
        invalidateDesignQueries({ organizationId }),
      ])
    },
  })
}

export function useUpdateAlbumMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: { albumId: AlbumIdInput; values: UpdateAlbumInput }) =>
      updateAlbum({ organizationId, ...values }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: albumQueryKeys.all({ organizationId }) }),
        queryClient.invalidateQueries({
          queryKey: designQueryKeys.default({ organizationId }),
          refetchType: 'all',
        }),
        invalidateDesignQueries({ organizationId }),
      ])
    },
  })
}

const cleanupDeletedAlbumQueries = ({
  organizationId,
  albumId,
}: {
  organizationId: number
  albumId: AlbumIdInput
}) => {
  // removeQueries is synchronous, so it runs before the invalidations instead of inside them.
  queryClient.removeQueries({
    queryKey: albumQueryKeys.show({ organizationId, albumId }),
  })

  return Promise.all([
    queryClient.invalidateQueries({ queryKey: albumQueryKeys.all({ organizationId }) }),
    queryClient.invalidateQueries({
      queryKey: designQueryKeys.default({ organizationId }),
      refetchType: 'all',
    }),
    invalidateDesignQueries({ organizationId }),
  ])
}

export function useDeleteAlbumMutation({
  cleanupOnSuccess = true,
}: { cleanupOnSuccess?: boolean } = {}) {
  const organizationId = useRouteOrganizationId()
  const cleanupDeletedAlbum = (albumId: AlbumIdInput) =>
    cleanupDeletedAlbumQueries({ organizationId, albumId })

  const mutation = useMutation({
    mutationFn: (albumId: AlbumIdInput) => deleteAlbum({ organizationId, albumId }),
    onSuccess: (_, albumId) => {
      if (!cleanupOnSuccess) {
        return undefined
      }

      return cleanupDeletedAlbum(albumId)
    },
  })

  return {
    ...mutation,
    cleanupDeletedAlbum,
  }
}
